/**
 * Platform-wide primary CTA intents for Nav / Hero.
 *
 * Routing verbs only — marketing intent lives in the button label:
 * - section → in-page hash (#reserve, #menu, …)
 * - external → leave-site URL (Lieferando, Resmio, …)
 * - call → telephone
 */

import { buildLocalizedRecord, type ProjectLocalePolicy } from '../locales/platformLocales';
import { FAMILY_ID, FAMILY_PRIMARY_CTA, type FamilyId, type FamilyPrimaryCta } from './familyRecipes';

export const PLATFORM_CTA_INTENT = {
  section: 'section',
  external: 'external',
  call: 'call',
} as const;

export const PLATFORM_CTA_INTENT_VALUES = [
  PLATFORM_CTA_INTENT.section,
  PLATFORM_CTA_INTENT.external,
  PLATFORM_CTA_INTENT.call,
] as const;

export type PlatformCtaIntent = (typeof PLATFORM_CTA_INTENT_VALUES)[number];

/**
 * Legacy demand verbs / section aliases still accepted from stored content.
 * Normalized to section | external | call; the stored href keeps the destination.
 */
export const LEGACY_CTA_INTENT_ALIASES = {
  book: PLATFORM_CTA_INTENT.section,
  browse: PLATFORM_CTA_INTENT.section,
  inquire: PLATFORM_CTA_INTENT.section,
  visit: PLATFORM_CTA_INTENT.section,
  reserve: PLATFORM_CTA_INTENT.section,
  menu: PLATFORM_CTA_INTENT.section,
  contact: PLATFORM_CTA_INTENT.section,
  route: PLATFORM_CTA_INTENT.section,
  order: PLATFORM_CTA_INTENT.external,
} as const;

export type LegacyCtaIntentAlias = keyof typeof LEGACY_CTA_INTENT_ALIASES;

const FAMILY_PRIMARY_TO_PLATFORM: Record<FamilyPrimaryCta, PlatformCtaIntent> = {
  [FAMILY_PRIMARY_CTA.reserve]: PLATFORM_CTA_INTENT.section,
  [FAMILY_PRIMARY_CTA.order]: PLATFORM_CTA_INTENT.external,
  [FAMILY_PRIMARY_CTA.call]: PLATFORM_CTA_INTENT.call,
  [FAMILY_PRIMARY_CTA.route]: PLATFORM_CTA_INTENT.section,
};

/** All families get the same three routing actions. */
export const FAMILY_ALLOWED_CTA_INTENTS: Record<FamilyId, readonly PlatformCtaIntent[]> = {
  [FAMILY_ID.fineDining]: PLATFORM_CTA_INTENT_VALUES,
  [FAMILY_ID.bistro]: PLATFORM_CTA_INTENT_VALUES,
  [FAMILY_ID.asian]: PLATFORM_CTA_INTENT_VALUES,
  [FAMILY_ID.cafe]: PLATFORM_CTA_INTENT_VALUES,
  [FAMILY_ID.bar]: PLATFORM_CTA_INTENT_VALUES,
  [FAMILY_ID.foodTruck]: PLATFORM_CTA_INTENT_VALUES,
  [FAMILY_ID.imbiss]: PLATFORM_CTA_INTENT_VALUES,
};

export function isPlatformCtaIntent(value: string | null | undefined): value is PlatformCtaIntent {
  return Boolean(value && (PLATFORM_CTA_INTENT_VALUES as readonly string[]).includes(value));
}

/** Normalize stored / legacy / family seed values to a platform intent. */
export function normalizePlatformCtaIntent(
  value: string | null | undefined,
): PlatformCtaIntent | null {
  if (!value) {
    return null;
  }
  const trimmed = value.trim();
  if (isPlatformCtaIntent(trimmed)) {
    return trimmed;
  }
  if (trimmed in LEGACY_CTA_INTENT_ALIASES) {
    return LEGACY_CTA_INTENT_ALIASES[trimmed as LegacyCtaIntentAlias];
  }
  return null;
}

export function mapFamilyPrimaryCtaToPlatform(
  primary: FamilyPrimaryCta | string | null | undefined,
): PlatformCtaIntent {
  if (primary && primary in FAMILY_PRIMARY_TO_PLATFORM) {
    return FAMILY_PRIMARY_TO_PLATFORM[primary as FamilyPrimaryCta];
  }
  return normalizePlatformCtaIntent(primary) ?? PLATFORM_CTA_INTENT.section;
}

export function listAllowedCtaIntentsForFamily(familyId: FamilyId): readonly PlatformCtaIntent[] {
  return FAMILY_ALLOWED_CTA_INTENTS[familyId] ?? PLATFORM_CTA_INTENT_VALUES;
}

/**
 * Default destination written when the owner picks an action.
 * Section falls back to `#reserve` — Zone 4 should prefer a live site section when known.
 */
export function defaultHrefForCtaIntent(intent: PlatformCtaIntent): string {
  switch (intent) {
    case PLATFORM_CTA_INTENT.section:
      return '/#reserve';
    case PLATFORM_CTA_INTENT.call:
    case PLATFORM_CTA_INTENT.external:
      return '';
    default:
      return '/#contact';
  }
}

/**
 * External needs an owner-supplied URL.
 * Call needs a phone number (stored as tel:…).
 * Section needs an in-page hash chosen from site blocks.
 */
export function ctaIntentNeedsExternalDestination(intent: PlatformCtaIntent): boolean {
  return intent === PLATFORM_CTA_INTENT.external;
}

export function ctaIntentNeedsPhoneDestination(intent: PlatformCtaIntent): boolean {
  return intent === PLATFORM_CTA_INTENT.call;
}

export function ctaIntentNeedsSectionDestination(intent: PlatformCtaIntent): boolean {
  return intent === PLATFORM_CTA_INTENT.section;
}

/**
 * Infer intent from a stored href when `ctaIntent` is missing (legacy projects).
 */
export function inferCtaIntentFromHref(href: string | null | undefined): PlatformCtaIntent | null {
  const h = (href ?? '').trim().toLowerCase();
  if (!h) {
    return null;
  }
  if (h.startsWith('tel:')) {
    return PLATFORM_CTA_INTENT.call;
  }
  if (/^https?:\/\//.test(h) || h.includes('wa.me') || h.includes('whatsapp')) {
    return PLATFORM_CTA_INTENT.external;
  }
  if (h.includes('#') || h.startsWith('/')) {
    return PLATFORM_CTA_INTENT.section;
  }
  return null;
}

/** Seed `ctaIntent` as a locale-mirrored content field (same pattern as navPresentation). */
export function seedCtaIntentContent(
  localePolicy: ProjectLocalePolicy,
  intent: PlatformCtaIntent,
): Record<string, string> {
  return buildLocalizedRecord(localePolicy.defaultLocale, localePolicy.supportedLocales, {
    de: intent,
    en: intent,
    vi: intent,
  });
}
